import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, BarChart3, CalendarDays, Loader2 } from 'lucide-react';
import Drawer from '../components/Drawer';
import Footer from '../components/Footer';
import StyledSelect from '../components/StyledSelect';
import FlatpickrInput from '../components/FlatpickrInput';
import { getProjectHistoryAnalytics, getProjectsList } from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import { useActiveProject } from '../context/ActiveProjectContext';
import { useAuth } from '../context/AuthContext';

const cardCls = 'rounded-3xl border bg-white p-5 shadow-sm';

export default function HistoryAnalyticsPage() {
  const { currency } = useCurrency();
  const { selectedProjectId } = useActiveProject();
  const { canViewProjectHistory } = useAuth();

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState({ totals: null, monthly: [], actions: [], by_project: [] });

  const projectOptions = useMemo(
    () => [{ value: '', label: 'Active Project' }, ...projects.map((p) => ({ value: String(p.id), label: p.name }))],
    [projects]
  );

  useEffect(() => {
    if (!canViewProjectHistory) return;
    const load = async () => {
      const res = await getProjectsList();
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setProjects(list);
      if (selectedProjectId) setProjectId(String(selectedProjectId));
    };
    load();
  }, [selectedProjectId, canViewProjectHistory]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (projectId) params.project_id = projectId;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await getProjectHistoryAnalytics(params);
      setAnalytics(res?.data?.data || { totals: null, monthly: [], actions: [], by_project: [] });
    } catch (err) {
      setAnalytics({ totals: null, monthly: [], actions: [], by_project: [] });
      setError(err?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewProjectHistory) return;
    loadAnalytics();
  }, [projectId, canViewProjectHistory]);

  if (!canViewProjectHistory) {
    const deniedContent = (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-3xl border bg-white p-6 text-center" style={{ borderColor: '#f2d8e0' }}>
          <h1 className="text-2xl font-bold text-slate-900">History Analytics</h1>
          <p className="mt-2 text-sm text-slate-500">
            You do not have permission to view organization-wide history analytics.
          </p>
        </div>
      </div>
    );

    return <Drawer pageHeader={deniedContent} />;
  }

  const totals = analytics.totals || {
    total_rows: 0,
    total_transactions: 0,
    income: 0,
    expense: 0,
    profit: 0,
    loss: 0,
    avg_amount: 0,
    max_amount: 0,
    min_amount: 0,
  };

  const cards = [
    { label: 'Income', value: totals.income, color: '#16a34a' },
    { label: 'Expense', value: totals.expense, color: '#c44569' },
    { label: 'Profit', value: totals.profit, color: totals.profit >= 0 ? '#0f766e' : '#b91c1c' },
    { label: 'Transactions', value: totals.total_transactions, color: '#334155', plain: true },
  ];

  const primaryCurrency = useMemo(() => {
    const rows = analytics.currency_totals || [];
    if (!Array.isArray(rows) || rows.length === 0) return (currency?.code || 'PKR');
    if (rows.length === 1) return rows[0].currency || 'PKR';
    return 'MIXED';
  }, [analytics, currency]);

  const formatMoney = (value, code = 'PKR') => `${code} ${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const pageContent = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 rounded-3xl border p-5 bg-white shadow-sm" style={{ borderColor: '#f2d8e0' }}>
        <h1 className="text-2xl font-bold text-slate-900">Transaction History Analytics</h1>
        <p className="text-sm text-slate-500 mt-1">Professional analytics from transaction history with charts and detailed financial breakdown.</p>
      </div>

      <div className={`${cardCls} mb-6`} style={{ borderColor: '#f3dbe3' }}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-2">Project</label>
            <StyledSelect
              value={projectId}
              onChange={setProjectId}
              options={projectOptions}
              placeholder="Active Project"
              searchable
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-2">Start Date</label>
            <FlatpickrInput value={startDate} onChange={setStartDate} options={{ dateFormat: 'Y-m-d' }} className="!bg-white" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-2">End Date</label>
            <FlatpickrInput value={endDate} onChange={setEndDate} options={{ dateFormat: 'Y-m-d' }} className="!bg-white" />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={loadAnalytics}
              disabled={loading}
              className="h-12 w-full rounded-xl text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
              style={{ background: '#c44569' }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Activity size={16} />}
              Refresh Analytics
            </button>
          </div>
        </div>

        {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((card) => (
          <div key={card.label} className={`${cardCls}`} style={{ borderColor: '#f2dce3' }}>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{card.label}</p>
            <p className="mt-2 text-xl font-bold" style={{ color: card.color }}>
              {card.plain ? card.value : formatMoney(card.value || 0, primaryCurrency)}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <section className={cardCls} style={{ borderColor: '#f2dce3' }}>
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays size={16} color="#c44569" />
            <h2 className="font-bold text-slate-800">Monthly Income vs Expense</h2>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="income" stroke="#16a34a" strokeWidth={2} />
                <Line type="monotone" dataKey="expense" stroke="#c44569" strokeWidth={2} />
                <Line type="monotone" dataKey="profit" stroke="#0f766e" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className={cardCls} style={{ borderColor: '#f2dce3' }}>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={16} color="#0f766e" />
            <h2 className="font-bold text-slate-800">Change Actions Distribution</h2>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={analytics.actions || []}
                  dataKey="total"
                  nameKey="action"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  fill="#c44569"
                  label
                />
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <section className={cardCls} style={{ borderColor: '#f2dce3' }}>
          <h2 className="font-bold text-slate-800 mb-4">Income/Expense by Project</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.by_project || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="project_name" hide />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="income" fill="#16a34a" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" fill="#c44569" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className={cardCls} style={{ borderColor: '#f2dce3' }}>
          <h2 className="font-bold text-slate-800 mb-4">Detailed Financial Summary</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Total History Rows</span><span className="font-semibold">{totals.total_rows}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Average Amount</span><span className="font-semibold">{formatMoney(totals.avg_amount, primaryCurrency)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Maximum Amount</span><span className="font-semibold">{formatMoney(totals.max_amount, primaryCurrency)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Minimum Amount</span><span className="font-semibold">{formatMoney(totals.min_amount, primaryCurrency)}</span></div>
            <div className="pt-2 border-t border-slate-100" />
            {(analytics.by_project || []).map((row) => (
              <div key={row.project_name} className="rounded-xl border border-slate-100 px-3 py-2 bg-slate-50/60">
                <p className="font-semibold text-slate-700">{row.project_name}</p>
                <p className="text-xs text-slate-500 mt-1">Income: {formatMoney(row.income, primaryCurrency)} · Expense: {formatMoney(row.expense, primaryCurrency)} · Entries: {row.total_rows}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );

  return <Drawer pageHeader={pageContent} />;
}
